import { httpsCallable } from 'firebase/functions';
import { appId, functions } from '@/lib/firebase';

export type DonorWallConsentLevel = 'anonymous' | 'firstNameOnly' | 'full';

export interface RecentDonorFeedEntry {
  id: string;
  firstNameOrAlias: string | null;
  country: string;
  amount: number;
  currency: string;
  consentLevel: DonorWallConsentLevel;
  donationFrequency: 'one_time' | 'monthly';
  createdAtMs: number | null;
}

export function formatDonorFeedAmount(amountCents: number, currency: string): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: (currency || 'usd').toUpperCase(),
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format((amountCents || 0) / 100);
}

export function formatDonorFeedLine(entry: RecentDonorFeedEntry): string {
  const amountLabel = formatDonorFeedAmount(entry.amount, entry.currency);
  const location = entry.country && entry.country !== 'Global' ? entry.country : 'around the world';

  if (entry.consentLevel === 'anonymous' || !entry.firstNameOrAlias) {
    return `A supporter from ${location} just gave ${amountLabel}`;
  }

  return `${entry.firstNameOrAlias} from ${location} just gave ${amountLabel}`;
}

export async function fetchRecentDonorFeed(limit = 6): Promise<RecentDonorFeedEntry[]> {
  try {
    const call = httpsCallable(functions, 'getPublicRecentDonorFeed');
    const result = await call({ appId, limit });
    const data = result.data as { success?: boolean; entries?: RecentDonorFeedEntry[] };
    if (!Array.isArray(data.entries)) return [];
    return data.entries;
  } catch (error) {
    console.warn(
      `[fetchRecentDonorFeed] Failed to load recent donor feed for app "${appId}":`,
      error
    );
    return [];
  }
}
